import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WizardState,
  WizardMessage,
  UsePreferencesWizardReturn,
  INITIAL_WIZARD_STATE,
  StartWizardResponse,
  GetWizardResponse,
  WizardRespondResponse,
  WizardCompleteResponse,
  WizardAbandonResponse,
  ActiveSessionConflict,
} from '../types/preferencesWizard';

/**
 * Hook for managing the preferences wizard conversation.
 *
 * Provides functions to start, interact with, and complete/abandon
 * the AI-powered preferences wizard session.
 *
 * @example
 * ```tsx
 * const {
 *   state,
 *   isLoading,
 *   error,
 *   startWizard,
 *   sendMessage,
 *   completeWizard,
 *   abandonWizard,
 * } = usePreferencesWizard();
 *
 * // Start a new wizard session
 * await startWizard();
 *
 * // Send a user message
 * await sendMessage("I'm looking for a 3 bedroom house");
 *
 * // Complete and get synthesized preferences
 * const preferences = await completeWizard();
 * ```
 */
export function usePreferencesWizard(): UsePreferencesWizardReturn {
  const navigate = useNavigate();
  const [state, setState] = useState<WizardState>(INITIAL_WIZARD_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSessionConflict, setActiveSessionConflict] = useState<ActiveSessionConflict | null>(null);

  /**
   * Handle unauthorized responses by redirecting to login
   */
  const handleUnauthorizedResponse = useCallback((response: Response): boolean => {
    if (response.status === 401) {
      navigate('/login');
      return true;
    }
    return false;
  }, [navigate]);

  /**
   * Clear any error state and session conflict
   */
  const clearError = useCallback(() => {
    setError(null);
    setActiveSessionConflict(null);
  }, []);

  /**
   * Start a new wizard session
   * @param maxRounds Optional maximum number of conversation rounds (default: 10)
   * @returns true if successful, false otherwise
   */
  const startWizard = useCallback(async (maxRounds?: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/account/preferences-wizard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: maxRounds ? JSON.stringify({ maxRounds }) : undefined,
      });

      if (handleUnauthorizedResponse(response)) {
        return false;
      }

      const data: StartWizardResponse = await response.json();

      if (!data.success) {
        // Check if this is an active session conflict error
        if (data.errorCode === 'ACTIVE_SESSION_EXISTS' && data.existingSessionId) {
          setActiveSessionConflict({ sessionId: data.existingSessionId });
        }
        setError(data.error || 'Failed to start wizard');
        return false;
      }

      // Clear any previous conflict state on success
      setActiveSessionConflict(null);

      // Create the initial assistant message from the opening message
      const initialMessage: WizardMessage = {
        id: Date.now(), // Temporary ID until we refresh from server
        role: 'assistant',
        content: data.openingMessage || '',
        createdAt: new Date().toISOString(),
      };

      setState({
        isActive: true,
        sessionId: data.sessionId || null,
        conversationUuid: data.conversationUuid || null,
        messages: [initialMessage],
        currentRound: data.currentRound || 0,
        maxRounds: data.maxRounds || 10,
        isComplete: false,
        suggestedResponses: undefined,
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start wizard';
      setError(errorMessage);
      console.error('Error starting preferences wizard:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [handleUnauthorizedResponse]);

  /**
   * Send a user message to the wizard
   * @param message The user's message content
   * @returns true if successful, false otherwise
   */
  const sendMessage = useCallback(async (message: string): Promise<boolean> => {
    if (!state.isActive || !state.sessionId) {
      setError('No active wizard session');
      return false;
    }

    if (!message.trim()) {
      setError('Message cannot be empty');
      return false;
    }

    setIsLoading(true);
    setError(null);

    // Optimistically add user message to state
    const userMessage: WizardMessage = {
      id: Date.now(),
      role: 'user',
      content: message.trim(),
      createdAt: new Date().toISOString(),
    };

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
    }));

    try {
      const response = await fetch('/api/account/preferences-wizard/respond', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          sessionId: state.sessionId,
          message: message.trim(),
        }),
      });

      if (handleUnauthorizedResponse(response)) {
        // Revert optimistic update on auth failure
        setState(prev => ({
          ...prev,
          messages: prev.messages.filter(m => m.id !== userMessage.id),
        }));
        return false;
      }

      const data: WizardRespondResponse = await response.json();

      if (!data.success) {
        // Revert optimistic update on error
        setState(prev => ({
          ...prev,
          messages: prev.messages.filter(m => m.id !== userMessage.id),
        }));
        setError(data.error || 'Failed to send message');
        return false;
      }

      // Add the assistant's response message
      const assistantMessage: WizardMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: data.message || '',
        createdAt: new Date().toISOString(),
      };

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage],
        currentRound: data.currentRound ?? prev.currentRound,
        maxRounds: data.maxRounds ?? prev.maxRounds,
        isComplete: data.isComplete ?? false,
        suggestedResponses: data.suggestedResponses,
      }));

      return true;
    } catch (err) {
      // Revert optimistic update on error
      setState(prev => ({
        ...prev,
        messages: prev.messages.filter(m => m.id !== userMessage.id),
      }));
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      console.error('Error sending message to preferences wizard:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [state.isActive, state.sessionId, handleUnauthorizedResponse]);

  /**
   * Complete the wizard and get synthesized preferences
   * @returns The synthesized preferences text, or null on failure
   */
  const completeWizard = useCallback(async (): Promise<string | null> => {
    if (!state.isActive || !state.sessionId) {
      setError('No active wizard session');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/account/preferences-wizard/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          sessionId: state.sessionId,
        }),
      });

      if (handleUnauthorizedResponse(response)) {
        return null;
      }

      const data: WizardCompleteResponse = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to complete wizard');
        return null;
      }

      // Mark the wizard as complete and inactive
      setState(prev => ({
        ...prev,
        isActive: false,
        isComplete: true,
      }));

      return data.preferences || null;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete wizard';
      setError(errorMessage);
      console.error('Error completing preferences wizard:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [state.isActive, state.sessionId, handleUnauthorizedResponse]);

  /**
   * Abandon the current wizard session without saving
   * @returns true if successful, false otherwise
   */
  const abandonWizard = useCallback(async (): Promise<boolean> => {
    if (!state.isActive || !state.sessionId) {
      // Nothing to abandon, just reset state
      setState(INITIAL_WIZARD_STATE);
      return true;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/account/preferences-wizard', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          sessionId: state.sessionId,
        }),
      });

      if (handleUnauthorizedResponse(response)) {
        return false;
      }

      const data: WizardAbandonResponse = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to abandon wizard');
        return false;
      }

      // Reset to initial state
      setState(INITIAL_WIZARD_STATE);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to abandon wizard';
      setError(errorMessage);
      console.error('Error abandoning preferences wizard:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [state.isActive, state.sessionId, handleUnauthorizedResponse]);

  /**
   * Refresh the current wizard state from the server
   * Useful for restoring state on page reload
   */
  const refreshState = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/account/preferences-wizard', {
        method: 'GET',
        credentials: 'same-origin',
      });

      if (handleUnauthorizedResponse(response)) {
        return;
      }

      const data: GetWizardResponse = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to refresh wizard state');
        return;
      }

      if (!data.isActive) {
        // No active session, reset to initial state
        setState(INITIAL_WIZARD_STATE);
        return;
      }

      // Update state with server data
      setState({
        isActive: true,
        sessionId: data.sessionId || null,
        conversationUuid: data.conversationUuid || null,
        messages: data.messages || [],
        currentRound: data.currentRound || 0,
        maxRounds: data.maxRounds || 10,
        isComplete: false,
        suggestedResponses: undefined,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh wizard state';
      setError(errorMessage);
      console.error('Error refreshing preferences wizard state:', err);
    } finally {
      setIsLoading(false);
    }
  }, [handleUnauthorizedResponse]);

  /**
   * Resume an existing session when an active session conflict is detected
   * @returns true if successful, false otherwise
   */
  const resumeExistingSession = useCallback(async (): Promise<boolean> => {
    if (!activeSessionConflict) return false;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch the active session state from the server
      const response = await fetch('/api/account/preferences-wizard', {
        method: 'GET',
        credentials: 'same-origin',
      });

      if (handleUnauthorizedResponse(response)) {
        return false;
      }

      const data: GetWizardResponse = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to resume session');
        setActiveSessionConflict(null); // Clear conflict to allow error display
        return false;
      }

      if (!data.isActive) {
        // Session no longer exists, clear conflict state
        setActiveSessionConflict(null);
        setState(INITIAL_WIZARD_STATE);
        return false;
      }

      // Update state with server data
      setState({
        isActive: true,
        sessionId: data.sessionId || null,
        conversationUuid: data.conversationUuid || null,
        messages: data.messages || [],
        currentRound: data.currentRound || 0,
        maxRounds: data.maxRounds || 10,
        isComplete: false,
        suggestedResponses: undefined,
      });

      setActiveSessionConflict(null);
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resume session';
      setError(errorMessage);
      setActiveSessionConflict(null); // Clear conflict to allow error display
      console.error('Error resuming existing session:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [activeSessionConflict, handleUnauthorizedResponse]);

  /**
   * Abandon the existing session and start a fresh one
   * @param maxRounds Optional maximum number of conversation rounds
   * @returns true if successful, false otherwise
   */
  const startFreshSession = useCallback(async (maxRounds?: number): Promise<boolean> => {
    if (!activeSessionConflict) {
      // No conflict, just start normally
      return startWizard(maxRounds);
    }

    setIsLoading(true);
    setError(null);

    try {
      // First, abandon the existing session
      const abandonResponse = await fetch('/api/account/preferences-wizard', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          sessionId: activeSessionConflict.sessionId,
        }),
      });

      if (handleUnauthorizedResponse(abandonResponse)) {
        setIsLoading(false);
        return false;
      }

      const abandonData: WizardAbandonResponse = await abandonResponse.json();

      if (!abandonData.success) {
        setError(abandonData.error || 'Failed to abandon existing session');
        setActiveSessionConflict(null); // Clear conflict to allow error display
        setIsLoading(false);
        return false;
      }

      // Clear the conflict state
      setActiveSessionConflict(null);

      // Set isLoading to false before calling startWizard, which will manage its own loading state
      setIsLoading(false);

      // Now start a fresh session - startWizard will manage its own loading state
      return startWizard(maxRounds);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start fresh session';
      setError(errorMessage);
      setActiveSessionConflict(null); // Clear conflict to allow error display
      console.error('Error starting fresh session:', err);
      setIsLoading(false);
      return false;
    }
  }, [activeSessionConflict, startWizard, handleUnauthorizedResponse]);

  return {
    state,
    isLoading,
    error,
    activeSessionConflict,
    startWizard,
    sendMessage,
    completeWizard,
    abandonWizard,
    refreshState,
    clearError,
    resumeExistingSession,
    startFreshSession,
  };
}
